if (!customElements.get('gift-quick-view')) {
  customElements.define(
    'gift-quick-view',
    class GiftQuickView extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog || typeof this.dialog.showModal !== 'function' || !window.AbortController) return;
        this.content = this.querySelector('[data-gift-content]');
        this.status = this.querySelector('[data-gift-status]');
        this.heading = this.querySelector('#GiftQuickViewTitle');
        this.recovery = this.querySelector('[data-gift-recovery]');
        this.fallback = this.querySelector('[data-gift-fallback]');
        this.cache = new Map();
        this.requestId = 0;
        this.onDocumentClick = (event) => {
          const link = event.target.closest('a[data-gift-quickview]');
          if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
          const url = new URL(link.href);
          if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
          event.preventDefault();
          this.open(link, url);
        };
        this.onDialogClick = (event) => {
          if (event.target.closest('[data-gift-close]')) this.dialog.close();
          else if (event.target.closest('[data-gift-retry]')) this.load();
          else if (event.target.closest('[data-gift-image]')) this.selectImage(event.target.closest('[data-gift-image]'));
          else if (event.target === this.dialog) {
            const box = this.dialog.getBoundingClientRect();
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
          }
        };
        this.onClose = () => {
          this.requestId++;
          this.controller?.abort();
          clearTimeout(this.timeout);
          if (!this.alreadyLocked) document.body.classList.remove('overflow-hidden');
          if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
          this.content.replaceChildren();
        };
        this.onKeyDown = (event) => {
          if (event.key !== 'Tab') return;
          const targets = Array.from(this.dialog.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')).filter(
            (element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
          );
          const first = targets[0];
          const last = targets[targets.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        };
        document.addEventListener('click', this.onDocumentClick);
        this.dialog.addEventListener('click', this.onDialogClick);
        this.dialog.addEventListener('close', this.onClose);
        this.dialog.addEventListener('keydown', this.onKeyDown);
      }

      disconnectedCallback() {
        this.controller?.abort();
        clearTimeout(this.timeout);
        document.removeEventListener('click', this.onDocumentClick);
        this.dialog?.removeEventListener('click', this.onDialogClick);
        this.dialog?.removeEventListener('close', this.onClose);
        this.dialog?.removeEventListener('keydown', this.onKeyDown);
        if (this.dialog?.open && !this.alreadyLocked) document.body.classList.remove('overflow-hidden');
      }

      open(opener, url) {
        this.opener = opener;
        this.url = url;
        this.fallback.href = url.href;
        this.heading.textContent = opener.textContent.trim();
        if (!this.dialog.open) {
          this.alreadyLocked = document.body.classList.contains('overflow-hidden');
          document.body.classList.add('overflow-hidden');
          this.dialog.showModal();
        }
        this.load();
      }

      async load() {
        this.controller?.abort();
        clearTimeout(this.timeout);
        const requestId = ++this.requestId;
        this.content.replaceChildren();
        this.recovery.hidden = true;
        this.status.textContent = this.dataset.loadingText;
        this.content.setAttribute('aria-busy', 'true');
        const url = new URL(this.url);
        const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
        url.searchParams.set('section_id', 'gift-product-quick-view');
        const controller = new AbortController();
        this.controller = controller;
        this.timeout = setTimeout(() => controller.abort(), 20000);
        try {
          let markup = this.cache.get(url.href);
          if (!markup) {
            const response = await fetch(url.href, { signal: controller.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            markup = await response.text();
          }
          if (!this.dialog.open || requestId !== this.requestId || controller.signal.aborted) return;
          const preview = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-gift-preview]');
          if (!preview?.dataset.productId || !preview.dataset.title) throw new Error('Invalid product preview');
          preview.querySelectorAll('script').forEach((script) => script.remove());
          this.content.replaceChildren(document.importNode(preview, true));
          this.heading.textContent = preview.dataset.title;
          this.status.textContent = '';
          this.dialog.scrollTop = 0;
          if (!this.cache.has(url.href)) {
            if (this.cache.size >= 4) this.cache.delete(this.cache.keys().next().value);
            this.cache.set(url.href, markup);
          }
        } catch (error) {
          if (this.dialog.open && requestId === this.requestId) {
            this.status.textContent = this.dataset.errorText;
            this.recovery.hidden = false;
          }
        } finally {
          if (requestId === this.requestId) {
            clearTimeout(this.timeout);
            this.content.setAttribute('aria-busy', 'false');
          }
        }
      }

      selectImage(button) {
        const image = button.querySelector('template')?.content.cloneNode(true);
        const target = this.content.querySelector('[data-gift-main-image]');
        if (!image || !target) return;
        target.replaceChildren(image);
        this.content.querySelectorAll('[data-gift-image]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      }
    }
  );
}
